import React from "react";
import PropTypes from "prop-types";
import PaymentIdentityFieldset from "app/pages/Booking/Payment/PaymentIdentityFieldset";
import "./payment-passengers-fieldset.scss";
import { ALERT_TYPE, BOOK_STATUS, PASSENGER_TYPE, RESOLUTION } from "app/constants";
import { FormattedDate, FormattedMessage } from "react-intl";
import DateSelectorInput from "app/pages/.shared/DateSelector/DateSelectorInput";
import RelativeLink from "app/pages/.shared/RelativeLink";
import { resolutionPropType } from "app/utils/propTypes";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { scrollToElement } from "app/utils/scroller";
import FidelityCardNumberInputContainer from "app/pages/.shared/FidelityCardNumberInputContainer";

class PaymentPassengersFieldset extends React.Component {
	componentDidUpdate() {
		if (
			this.props.showError &&
			this.props.bookStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY
		) {
			scrollToElement(this.childrenAgeDiscrepancyErrorPlaceholder, -14);
		} else if (
			this.props.showError &&
			this.props.bookStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY
		) {
			scrollToElement(this.infantsAgeDiscrepancyErrorPlaceholder, -14);
		}
	}

	render() {
		const {
			passengers = [],
			isChildrenAgeRequired,
			dateOfBirthRequired,
			showFrequentTravellerCardInput,
			resolution,
			productUri,
			showError = false,
			error = {},
			bookStatus,
			onBlur,
		} = this.props;

		const linkToSearchEngine =
			resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL
				? "/booking/search"
				: "/" + productUri;

		return (
			<div className="payment-passengers-fieldset">
				{passengers.map(
					(
						{ name, birthdate, type, frequentFlyerTravellerCardNumber, ...passenger },
						index
					) => {
						const dateNaissance = new Date(
							birthdate.year.value,
							birthdate.month.value,
							birthdate.day.value,
							15,
							0,
							0
						);

						let label = (
							<FormattedMessage
								id="payment.passengers.adult"
								values={{ index: passenger.index.value }}
							/>
						);

						if (type.value === PASSENGER_TYPE.CHILD) {
							label = isChildrenAgeRequired ? (
								<FormattedMessage
									id="payment.passengers.child.with.birthdate"
									values={{
										date: (
											<span className="payment-passengers-fieldset__birthdate-label">
												<FormattedDate
													value={dateNaissance}
													day="2-digit"
													month="long"
													year="numeric"
												/>
											</span>
										),
									}}
								/>
							) : (
								<FormattedMessage
									values={{ index: passenger.index.value }}
									id="payment.passengers.child"
								/>
							);
						} else if (type.value === PASSENGER_TYPE.INFANT) {
							label = isChildrenAgeRequired ? (
								<FormattedMessage
									id="payment.passengers.infant.with.birthdate"
									values={{
										date: (
											<span className="payment-passengers-fieldset__birthdate-label">
												<FormattedDate
													value={dateNaissance}
													day="2-digit"
													month="long"
													year="numeric"
												/>
											</span>
										),
									}}
								/>
							) : (
								<FormattedMessage
									values={{ index: passenger.index.value }}
									id="payment.passengers.infant"
								/>
							);
						}

						return (
							<div className="payment-passengers-fieldset__item" key={index}>
								{type.value === PASSENGER_TYPE.CHILD &&
								passenger.index.value === 1 ? (
									<div
										ref={target => {
											this.childrenAgeDiscrepancyErrorPlaceholder = target;
										}}
									>
										{bookStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY &&
										showError ? (
											<AlertMessage
												alertType={ALERT_TYPE.ERROR}
												message={
													<FormattedMessage
														{...error}
														values={{
															maxAge: isChildrenAgeRequired ? 17 : 11,
															link: (
																<RelativeLink
																	to={{
																		pathname: linkToSearchEngine,
																	}}
																	design="naked"
																>
																	<FormattedMessage id="error.no.avaibilities.link.label" />
																</RelativeLink>
															),
														}}
													/>
												}
											/>
										) : (
											false
										)}
									</div>
								) : (
									false
								)}

								{type.value === PASSENGER_TYPE.INFANT &&
								passenger.index.value === 1 ? (
									<div
										ref={target => {
											this.infantsAgeDiscrepancyErrorPlaceholder = target;
										}}
									>
										{bookStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY &&
										showError ? (
											<AlertMessage
												alertType={ALERT_TYPE.ERROR}
												message={
													<FormattedMessage
														{...error}
														values={{
															link: (
																<RelativeLink
																	to={{
																		pathname: linkToSearchEngine,
																	}}
																	design="naked"
																>
																	<FormattedMessage id="error.no.avaibilities.link.label" />
																</RelativeLink>
															),
														}}
													/>
												}
											/>
										) : (
											false
										)}
									</div>
								) : (
									false
								)}

								<div className="payment-passengers-fieldset__title">{label}</div>

								<PaymentIdentityFieldset
									fields={name}
									id={`passenger-${index}`}
									onBlur={onBlur}
								/>

								{type.value !== PASSENGER_TYPE.ADULT || dateOfBirthRequired ? (
									<div className="payment-passengers-fieldset__birthdate">
										<DateSelectorInput
											isRequired={true}
											isHidden={
												isChildrenAgeRequired &&
												(type.value === PASSENGER_TYPE.CHILD ||
													type.value === PASSENGER_TYPE.INFANT)
											}
											isChildrenAgeRequired={isChildrenAgeRequired}
											label={
												<FormattedMessage id="payment.pasengers.birthdate.label" />
											}
											fields={birthdate}
											id={`payment-passenger-child-${index}`}
											type={type.value}
											data-cy={`passenger-date-selector-${
												type.value
											}-${index}`}
										/>
									</div>
								) : (
									false
								)}

								{showFrequentTravellerCardInput && (
									<div className="payment-passengers-fieldset__frequent-flyer-traveller">
										<FidelityCardNumberInputContainer
											id={`passenger-${index}`}
											field={frequentFlyerTravellerCardNumber}
											onBlur={onBlur}
										/>
									</div>
								)}
							</div>
						);
					}
				)}
			</div>
		);
	}
}

PaymentPassengersFieldset.propTypes = {
	passengers: PropTypes.array,
	bookStatus: PropTypes.string,
	productUri: PropTypes.string,
	resolution: resolutionPropType,
	error: PropTypes.object,
	showError: PropTypes.bool,
	onBlur: PropTypes.func,
	isChildrenAgeRequired: PropTypes.bool,
	dateOfBirthRequired: PropTypes.bool,
	showFrequentTravellerCardInput: PropTypes.bool,
};

export default PaymentPassengersFieldset;
